import React, { useState, useEffect } from 'react';
import { bulkImportQuestions, createQuestion, getCategories, fetchZohoSheetData } from '../../lib/adminApi';

function AdminBulkImport() {
  const [sheetUrl, setSheetUrl] = useState('https://sheet.zoho.in/api/v2/wyumw4b37234aefe448da858618e16a273628');
  const [accessToken, setAccessToken] = useState('');
  const [categories, setCategories] = useState([]);
  const [fetchedData, setFetchedData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, success: 0, failed: [], currentQuestion: '' });
  const [result, setResult] = useState(null);
  const [categoryMapping, setCategoryMapping] = useState({});
  const [selectedCategories, setSelectedCategories] = useState({});
  const [selectedPreviewQuestions, setSelectedPreviewQuestions] = useState({});

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getCategories();
        setCategories(data.categories || []);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // Extract resource ID from Zoho Sheet URL
  const extractResourceId = (url) => {
    // URL formats:
    // https://sheet.zoho.in/api/v2/{resourceId}
    const patterns = [
      /\/api\/v2\/([a-zA-Z0-9]+)/,
      /resource_id=([a-zA-Z0-9]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const handleFetchData = async () => {
    setError(null);
    setFetchedData(null);
    setPreview(null);
    setResult(null);
    setLoading(true);

    try {
      const resourceId = extractResourceId(sheetUrl);
      if (!resourceId) {
        throw new Error('Could not extract resource ID from URL. Please check the URL format.');
      }

      // Fetch sheet data from backend (server-to-server call)
      const response = await fetchZohoSheetData(resourceId, accessToken);

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch sheet data');
      }

      // Parse the data
      const questions = parseSheetRecords(response.questions, 'Question');
      const answers = parseSheetRecords(response.answers, 'Answer');

      // Extract unique categories from questions
      const uniqueCategories = [...new Set(questions.map(q => q.Category).filter(Boolean))];

      // Initialize category mapping
      const initialMapping = {};
      uniqueCategories.forEach(cat => {
        // Try to find existing category with same name
        const existing = categories.find(c =>
          c.categoryName.toLowerCase() === cat.toLowerCase()
        );
        initialMapping[cat] = existing ? existing.categoryId : '';
      });
      setCategoryMapping(initialMapping);

      // Initialize all categories as unselected
      const initialSelection = {};
      uniqueCategories.forEach(cat => {
        initialSelection[cat] = false;
      });
      setSelectedCategories(initialSelection);

      setFetchedData({ questions, answers, uniqueCategories });

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Get list of selected category names
  const getSelectedCategoryList = () => {
    return Object.entries(selectedCategories)
      .filter(([_, isSelected]) => isSelected)
      .map(([cat]) => cat);
  };

  // Get questions from selected categories
  const getQuestionsFromSelectedCategories = () => {
    if (!fetchedData) return [];
    const selectedCats = getSelectedCategoryList();
    return fetchedData.questions.filter(q => selectedCats.includes(q.Category));
  };

  // Toggle category selection
  const toggleCategory = (category) => {
    setSelectedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Select/Deselect all categories
  const toggleAllCategories = (selectAll) => {
    const newSelection = {};
    fetchedData.uniqueCategories.forEach(cat => {
      newSelection[cat] = selectAll;
    });
    setSelectedCategories(newSelection);
  };

  // Count questions in a category
  const getQuestionCountByCategory = (category) => {
    if (!fetchedData) return 0;
    return fetchedData.questions.filter(q => q.Category === category).length;
  };

  // Count selected categories
  const selectedCategoryCount = Object.values(selectedCategories).filter(Boolean).length;

  // Count questions from selected categories
  const selectedQuestionCount = getQuestionsFromSelectedCategories().length;

  // Parse Zoho Sheet content from worksheet.content.get API
  // Response format: { range_details: [{ row_index, row_details: [{ column_index, content }] }] }
  // First row is headers, rest are data rows
  const parseSheetRecords = (data, sheetType) => {
    // Handle worksheet.content.get response format
    if (data && data.range_details && Array.isArray(data.range_details)) {
      const rows = data.range_details;
      if (rows.length < 2) {
        console.warn(`No data rows found in ${sheetType} sheet`);
        return [];
      }

      // Sort rows by row_index to ensure order
      rows.sort((a, b) => a.row_index - b.row_index);

      // First row is headers - extract content values by column_index
      const headerRow = rows[0].row_details || [];
      const headers = {};
      headerRow.forEach(cell => {
        headers[cell.column_index] = cell.content;
      });

      const records = [];

      // Convert remaining rows to objects using headers as keys
      for (let i = 1; i < rows.length; i++) {
        const rowDetails = rows[i].row_details || [];
        const record = {};

        rowDetails.forEach(cell => {
          const headerName = headers[cell.column_index];
          if (headerName) {
            record[headerName] = cell.content !== undefined ? cell.content : '';
          }
        });

        // Only add non-empty records
        if (Object.keys(record).length > 0) {
          records.push(record);
        }
      }

      return records;
    }

    // Fallback for records format
    if (data && data.records) {
      return data.records;
    }

    console.warn(`No records found in ${sheetType} sheet`);
    return [];
  };

  // Helper function to find a column value by trying multiple possible names (case-insensitive)
  const getColumnValue = (row, possibleNames) => {
    // First try exact matches
    for (const name of possibleNames) {
      if (row[name] !== undefined && row[name] !== null && row[name] !== '') {
        return row[name];
      }
    }
    // Then try case-insensitive match
    const rowKeys = Object.keys(row);
    for (const name of possibleNames) {
      const lowerName = name.toLowerCase().replace(/[_\s]/g, '');
      const matchedKey = rowKeys.find(k => k.toLowerCase().replace(/[_\s]/g, '') === lowerName);
      if (matchedKey && row[matchedKey] !== undefined && row[matchedKey] !== null && row[matchedKey] !== '') {
        return row[matchedKey];
      }
    }
    return '';
  };

  const handleGeneratePreview = () => {
    if (!fetchedData) return;

    setError(null);

    // Log column names for debugging
    if (fetchedData.questions.length > 0) {
      console.log('Question sheet columns:', Object.keys(fetchedData.questions[0]));
    }

    // Get questions from selected categories
    const questionsToImport = getQuestionsFromSelectedCategories();
    if (questionsToImport.length === 0) {
      setError('Please select at least one category');
      return;
    }

    // Check if all selected categories are mapped
    const selectedCats = getSelectedCategoryList();
    const unmappedCategories = selectedCats.filter(cat => !categoryMapping[cat]);
    if (unmappedCategories.length > 0) {
      setError(`Please map all categories: ${unmappedCategories.join(', ')}`);
      return;
    }

    // Group answers by Question ID
    const answersByQuestionId = {};
    fetchedData.answers.forEach(answer => {
      const qId = answer['Question ID'];
      if (!answersByQuestionId[qId]) {
        answersByQuestionId[qId] = [];
      }
      answersByQuestionId[qId].push(answer);
    });

    // Build preview data from questions in selected categories
    const previewData = questionsToImport.map((q, idx) => {
      const issues = [];
      const questionId = q['Question ID'];
      const category = q['Category'];
      const questionText = q['Question'];
      const template = q['Template'];
      const workingScript = q['Working script'];
      const statements = getColumnValue(q, ['Statements Used', 'Statements', 'Statement', 'STATEMENTS']);
      const connectionScopes = getColumnValue(q, ['Connection Scopes', 'Connections Used', 'ConnectionScopes', 'Connection_Scopes', 'CONNECTION_SCOPES']);
      const testCases = answersByQuestionId[questionId] || [];

      if (!questionText) issues.push('Missing question text');
      if (!category) issues.push('Missing category');
      if (!categoryMapping[category]) issues.push('Category not mapped');
      if (testCases.length === 0) issues.push('No test cases found');

      // Parse test cases
      const parsedTestCases = testCases.map(tc => {
        const inputValue = tc['Input (JSON)'] || '';

        // Parse the Output (JSON) which contains nested structure:
        // { "output": { "type": "...", "value": ... }, "code": ..., "message": ... }
        let outputType = 'string';
        let outputValue = '';

        const rawOutput = tc['Output (JSON)'];
        if (rawOutput) {
          try {
            // Try to parse as JSON
            const parsedOutput = typeof rawOutput === 'string' ? JSON.parse(rawOutput) : rawOutput;

            // Extract output from the nested structure
            // Handle both direct output and functions array format
            let outputObj = null;
            if (parsedOutput && parsedOutput.functions && Array.isArray(parsedOutput.functions) && parsedOutput.functions[0]) {
              // Format: { "functions": [{ "output": {...} }] }
              outputObj = parsedOutput.functions[0].output;
            } else if (parsedOutput && parsedOutput.output) {
              // Format: { "output": {...} }
              outputObj = parsedOutput.output;
            }

            if (outputObj) {
              outputType = outputObj.type || 'string';
              outputValue = typeof outputObj.value === 'object'
                ? JSON.stringify(outputObj.value)
                : String(outputObj.value || '');
            } else {
              // Fallback: use raw value
              outputValue = typeof parsedOutput === 'object'
                ? JSON.stringify(parsedOutput)
                : String(parsedOutput);
            }
          } catch (e) {
            // If JSON parsing fails, use raw value as string
            outputValue = String(rawOutput);
          }
        }

        return {
          inputValue,
          outputType,
          outputValue,
          hidden: String(tc['Hidden']).toLowerCase() === 'true' || tc['Hidden'] === true,
          explanation: tc['Explanation'] || '',
        };
      });

      return {
        index: idx + 1,
        originalQuestionId: questionId,
        question: questionText,
        categoryId: categoryMapping[category],
        categoryName: category,
        template: template || '',
        answer: workingScript || '',
        statements,
        connectionScopes,
        testCases: parsedTestCases,
        issues,
        valid: issues.length === 0,
      };
    });

    // Initialize all valid questions as selected
    const initialPreviewSelection = {};
    previewData.forEach(q => {
      initialPreviewSelection[q.index] = q.valid;
    });
    setSelectedPreviewQuestions(initialPreviewSelection);

    setPreview(previewData);
  };

  // Toggle preview question selection
  const togglePreviewQuestion = (index) => {
    setSelectedPreviewQuestions(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Select/Deselect all valid preview questions
  const toggleAllPreviewQuestions = (selectAll) => {
    const newSelection = {};
    preview.forEach(q => {
      newSelection[q.index] = selectAll && q.valid;
    });
    setSelectedPreviewQuestions(newSelection);
  };

  // Get selected valid questions count
  const getSelectedPreviewCount = () => {
    if (!preview) return 0;
    return preview.filter(q => selectedPreviewQuestions[q.index] && q.valid).length;
  };

  const handleImport = async () => {
    const selectedCount = getSelectedPreviewCount();
    if (selectedCount === 0) {
      setError('Please select at least one question to import');
      return;
    }

    setImporting(true);
    setError(null);
    setResult(null);

    // Prepare only selected valid questions for import
    const questionsToImport = preview
      .filter(q => selectedPreviewQuestions[q.index] && q.valid)
      .map(({ index, originalQuestionId, issues, valid, categoryName, ...q }) => q);

    const total = questionsToImport.length;
    let success = 0;
    const failed = [];

    setImportProgress({ current: 0, total, success: 0, failed: [], currentQuestion: '' });

    // Import questions one by one to show progress
    for (let i = 0; i < questionsToImport.length; i++) {
      const question = questionsToImport[i];
      const questionPreview = question.question?.substring(0, 50) + (question.question?.length > 50 ? '...' : '');

      setImportProgress(prev => ({
        ...prev,
        current: i + 1,
        currentQuestion: questionPreview
      }));

      try {
        await createQuestion(question);
        success++;
        setImportProgress(prev => ({ ...prev, success }));
      } catch (err) {
        console.error(`Failed to import question: ${questionPreview}`, err);
        failed.push({ question: questionPreview, error: err.message });
        setImportProgress(prev => ({ ...prev, failed: [...prev.failed, { question: questionPreview, error: err.message }] }));
      }
    }

    setImporting(false);

    if (failed.length === 0) {
      setResult({
        success: true,
        message: `Successfully imported ${success} questions`,
      });
      setSheetUrl('');
      setAccessToken('');
      setFetchedData(null);
      setPreview(null);
      setCategoryMapping({});
      setImportProgress({ current: 0, total: 0, success: 0, failed: [], currentQuestion: '' });
    } else {
      setResult({
        success: false,
        message: `Imported ${success} questions. Failed to import ${failed.length} questions.`,
        failed
      });
    }
  };

  const getCategoryName = (id) => {
    const cat = categories.find(c => c.categoryId === id);
    return cat ? cat.categoryName : `Unknown (${id})`;
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Bulk Import from Zoho Sheet</h1>

      {/* Instructions */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 mb-6">
        <h2 className="text-lg font-semibold text-white mb-3">Instructions</h2>
        <p className="text-gray-400 text-sm mb-4">
          Import questions from a Zoho Sheet. The sheet should have two worksheets:
        </p>

        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div className="bg-gray-900 p-3 rounded">
            <h3 className="text-blue-400 font-medium mb-2">Question Sheet (Columns)</h3>
            <ul className="text-gray-400 space-y-1">
              <li>Question ID</li>
              <li>Category</li>
              <li>Question</li>
              <li>Template</li>
              <li>Working script</li>
              <li>Statements Used</li>
              <li>Connection Scopes</li>
            </ul>
          </div>
          <div className="bg-gray-900 p-3 rounded">
            <h3 className="text-blue-400 font-medium mb-2">Answer Sheet (Columns)</h3>
            <ul className="text-gray-400 space-y-1">
              <li>Question ID</li>
              <li>TEST CASE ID</li>
              <li>Input (JSON)</li>
              <li>Output (JSON)</li>
              <li>Explanation</li>
              <li>Hidden</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Sheet URL & Token Input */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 mb-6">
        <h2 className="text-lg font-semibold text-white mb-3">Zoho Sheet Details</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Sheet URL</label>
            <input
              type="text"
              value={sheetUrl}
              onChange={(e) => setSheetUrl(e.target.value)}
              placeholder="https://sheet.zoho.com/sheet/open/..."
              className="w-full px-4 py-2 bg-gray-900 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Access Token</label>
            <input
              type="password"
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              placeholder="Enter Zoho OAuth access token"
              className="w-full px-4 py-2 bg-gray-900 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
            />
          </div>

          <button
            onClick={handleFetchData}
            disabled={!sheetUrl.trim() || !accessToken.trim() || loading}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Fetching...
              </>
            ) : (
              'Fetch Sheet Data'
            )}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-600/20 border border-red-500/30 rounded-lg p-4 mb-6 text-red-400">
          {error}
        </div>
      )}

      {/* Success */}
      {result?.success && (
        <div className="bg-green-600/20 border border-green-500/30 rounded-lg p-4 mb-6 text-green-400">
          {result.message}
        </div>
      )}

      {/* Fetched Data Summary & Category Selection */}
      {fetchedData && !preview && (
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 mb-6">
          <h2 className="text-lg font-semibold text-white mb-3">Fetched Data</h2>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-900 p-3 rounded">
              <div className="text-2xl font-bold text-blue-400">{fetchedData.uniqueCategories.length}</div>
              <div className="text-gray-400 text-sm">Categories</div>
            </div>
            <div className="bg-gray-900 p-3 rounded">
              <div className="text-2xl font-bold text-green-400">{fetchedData.questions.length}</div>
              <div className="text-gray-400 text-sm">Total Questions</div>
            </div>
            <div className="bg-gray-900 p-3 rounded">
              <div className="text-2xl font-bold text-purple-400">{fetchedData.answers.length}</div>
              <div className="text-gray-400 text-sm">Test Cases</div>
            </div>
          </div>

          {/* Category Selection & Mapping */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-md font-semibold text-white">Select Categories to Import</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => toggleAllCategories(true)}
                  className="px-3 py-1 text-sm bg-gray-700 text-white rounded hover:bg-gray-600"
                >
                  Select All
                </button>
                <button
                  onClick={() => toggleAllCategories(false)}
                  className="px-3 py-1 text-sm bg-gray-700 text-white rounded hover:bg-gray-600"
                >
                  Deselect All
                </button>
              </div>
            </div>

            <div className="bg-gray-900 rounded border border-gray-700">
              {fetchedData.uniqueCategories.map((cat) => {
                const questionCount = getQuestionCountByCategory(cat);
                const isSelected = selectedCategories[cat] || false;
                return (
                  <div
                    key={cat}
                    className="flex items-center gap-4 p-3 border-b border-gray-700 last:border-b-0"
                  >
                    <label className="flex items-center gap-3 cursor-pointer min-w-[200px]">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleCategory(cat)}
                        className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-white">{cat}</span>
                      <span className="text-xs text-gray-400">({questionCount} questions)</span>
                    </label>

                    {isSelected && (
                      <>
                        <span className="text-gray-500">→</span>
                        <select
                          value={categoryMapping[cat] || ''}
                          onChange={(e) => setCategoryMapping(prev => ({
                            ...prev,
                            [cat]: e.target.value || ''
                          }))}
                          className="flex-1 px-3 py-2 bg-gray-800 text-white rounded border border-gray-600"
                        >
                          <option value="">Map to category...</option>
                          {categories.map(c => (
                            <option key={c.categoryId} value={c.categoryId}>
                              {c.categoryName}
                            </option>
                          ))}
                        </select>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {selectedCategoryCount > 0 && (
            <div className="text-gray-400 text-sm mb-4">
              Selected: {selectedCategoryCount} categories, {selectedQuestionCount} questions
            </div>
          )}

          <button
            onClick={handleGeneratePreview}
            disabled={selectedCategoryCount === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            Generate Preview ({selectedQuestionCount} questions)
          </button>
        </div>
      )}

      {/* Import Progress Modal */}
      {importing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-lg w-full mx-4 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">Importing Questions...</h2>

            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm text-gray-300">
                <span>Progress: {importProgress.current} / {importProgress.total}</span>
                <span>{importProgress.total > 0 ? Math.round((importProgress.current / importProgress.total) * 100) : 0}%</span>
              </div>

              <div className="w-full bg-gray-700 rounded-full h-4 overflow-hidden">
                <div
                  className="h-full transition-all duration-300 ease-out"
                  style={{
                    width: `${importProgress.total > 0 ? (importProgress.current / importProgress.total) * 100 : 0}%`,
                    background: 'linear-gradient(90deg, #22c55e 0%, #4ade80 25%, #22c55e 50%, #4ade80 75%, #22c55e 100%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.5s infinite linear'
                  }}
                />
              </div>
              <style>{`
                @keyframes shimmer {
                  0% { background-position: 200% 0; }
                  100% { background-position: -200% 0; }
                }
              `}</style>

              <div className="text-sm text-gray-400">
                <div className="flex justify-between mb-1">
                  <span>Success: <span className="text-green-400">{importProgress.success}</span></span>
                  <span>Failed: <span className="text-red-400">{importProgress.failed.length}</span></span>
                </div>
              </div>

              {importProgress.currentQuestion && (
                <div className="p-2 bg-gray-900 rounded text-sm">
                  <span className="text-gray-500">Currently importing:</span>
                  <div className="text-gray-300 truncate">{importProgress.currentQuestion}</div>
                </div>
              )}

              {importProgress.failed.length > 0 && (
                <details className="text-sm">
                  <summary className="text-red-400 cursor-pointer hover:text-red-300">
                    View Failed ({importProgress.failed.length})
                  </summary>
                  <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                    {importProgress.failed.map((f, idx) => (
                      <div key={idx} className="p-2 bg-red-900/20 rounded text-red-300 text-xs">
                        {f.question}: {f.error}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden mb-6">
          <div className="p-4 border-b border-gray-700">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-semibold text-white">
                Preview ({preview.length} questions, {getSelectedPreviewCount()} selected)
              </h2>
              <div className="flex gap-3">
                <button
                  onClick={() => setPreview(null)}
                  disabled={importing}
                  className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 disabled:opacity-50"
                >
                  Back to Mapping
                </button>
                <button
                  onClick={handleImport}
                  disabled={importing || getSelectedPreviewCount() === 0}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {importing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Importing...
                    </>
                  ) : (
                    `Import Selected (${getSelectedPreviewCount()})`
                  )}
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => toggleAllPreviewQuestions(true)}
                disabled={importing}
                className="px-3 py-1 text-sm bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50"
              >
                Select All Valid
              </button>
              <button
                onClick={() => toggleAllPreviewQuestions(false)}
                disabled={importing}
                className="px-3 py-1 text-sm bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50"
              >
                Deselect All
              </button>
            </div>
          </div>

          <div className="divide-y divide-gray-700 max-h-[500px] overflow-y-auto">
            {preview.map(q => (
              <div key={q.index} className={`p-4 ${q.valid ? '' : 'bg-red-900/10'}`}>
                <div className="flex items-start gap-3 mb-2">
                  <input
                    type="checkbox"
                    checked={selectedPreviewQuestions[q.index] || false}
                    onChange={() => togglePreviewQuestion(q.index)}
                    disabled={!q.valid}
                    className="mt-1 w-4 h-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="text-gray-400 text-sm">
                          Question #{q.index} (Original ID: {q.originalQuestionId})
                        </span>
                        <h3 className="text-white font-medium">
                          {q.question?.substring(0, 100)}{q.question?.length > 100 ? '...' : ''}
                        </h3>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${q.valid ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'}`}>
                        {q.valid ? 'Valid' : 'Invalid'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 text-sm text-gray-400">
                  <span>Category: {q.categoryName} → {getCategoryName(q.categoryId)}</span>
                  <span>Test Cases: {q.testCases?.length || 0}</span>
                  <span>Hidden: {q.testCases?.filter(t => t.hidden).length || 0}</span>
                </div>

                {q.template && (
                  <details className="mt-2">
                    <summary className="text-blue-400 text-sm cursor-pointer hover:text-blue-300">
                      View Template
                    </summary>
                    <pre className="mt-1 p-2 bg-gray-900 rounded text-xs text-gray-400 overflow-x-auto">
                      {q.template}
                    </pre>
                  </details>
                )}

                {q.answer && (
                  <details className="mt-2">
                    <summary className="text-green-400 text-sm cursor-pointer hover:text-green-300">
                      View Answer (Working Script)
                    </summary>
                    <pre className="mt-1 p-2 bg-gray-900 rounded text-xs text-gray-400 overflow-x-auto">
                      {q.answer}
                    </pre>
                  </details>
                )}

                {q.statements && (
                  <details className="mt-2">
                    <summary className="text-yellow-400 text-sm cursor-pointer hover:text-yellow-300">
                      View Statements
                    </summary>
                    <pre className="mt-1 p-2 bg-gray-900 rounded text-xs text-gray-400 overflow-x-auto whitespace-pre-wrap">
                      {q.statements}
                    </pre>
                  </details>
                )}

                {q.connectionScopes && (
                  <details className="mt-2">
                    <summary className="text-cyan-400 text-sm cursor-pointer hover:text-cyan-300">
                      View Connection Scopes
                    </summary>
                    <pre className="mt-1 p-2 bg-gray-900 rounded text-xs text-gray-400 overflow-x-auto">
                      {q.connectionScopes}
                    </pre>
                  </details>
                )}

                {q.testCases && q.testCases.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-purple-400 text-sm cursor-pointer hover:text-purple-300">
                      View Test Cases ({q.testCases.length})
                    </summary>
                    <div className="mt-1 space-y-2">
                      {q.testCases.map((tc, idx) => (
                        <div key={idx} className="p-2 bg-gray-900 rounded text-xs">
                          <div className="flex gap-4 mb-1">
                            <span className="text-gray-500">#{idx + 1}</span>
                            <span className={`px-1.5 py-0.5 rounded ${tc.hidden ? 'bg-yellow-600/20 text-yellow-400' : 'bg-gray-700 text-gray-400'}`}>
                              {tc.hidden ? 'Hidden' : 'Visible'}
                            </span>
                            <span className="px-1.5 py-0.5 rounded bg-blue-600/20 text-blue-400">
                              Type: {tc.outputType}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mt-1">
                            <div>
                              <span className="text-gray-500">Input:</span>
                              <pre className="text-gray-400 whitespace-pre-wrap break-all">{tc.inputValue?.substring(0, 100)}{tc.inputValue?.length > 100 ? '...' : ''}</pre>
                            </div>
                            <div>
                              <span className="text-gray-500">Output:</span>
                              <pre className="text-gray-400 whitespace-pre-wrap break-all">{tc.outputValue?.substring(0, 100)}{tc.outputValue?.length > 100 ? '...' : ''}</pre>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}

                {!q.valid && (
                  <div className="mt-2 text-sm text-red-400">
                    Issues: {q.issues.join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminBulkImport;